import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import type { FlagshipEcosystemOrg } from "../data/flagshipXrpEcosystem";
import type { FlagshipOrg } from "../data/flagshipXrpOrgs";
import "./FlagshipRegionModal.css";

type FlagshipRegionModalProps = {
  org?: FlagshipOrg | null;
  regionLabel?: string;
  flagEmoji?: string;
  flagSrc?: string;
  ecosystem?: FlagshipEcosystemOrg | null;
  isOpen: boolean;
  onClose: () => void;
};

const FlagshipRegionModal: React.FC<FlagshipRegionModalProps> = ({
  org = null,
  regionLabel = "",
  flagEmoji = "",
  flagSrc,
  ecosystem = null,
  isOpen,
  onClose,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  const isEcosystem = Boolean(ecosystem);
  const panel = ecosystem ?? org;
  if (!isOpen || !panel) return null;

  const name = panel.shortName;
  const fullName = panel.name;
  const summary = panel.summary;
  const href = panel.href;
  const xHref = "xHref" in panel ? panel.xHref : undefined;
  const articleHref = org && !isEcosystem ? org.articleHref : undefined;
  const articleLabel = org && !isEcosystem ? org.articleLabel : undefined;
  const involveCta = org && !isEcosystem ? (org.involveCta ?? `Become part of ${org.shortName}.`) : undefined;
  const focuses = ecosystem?.focuses;
  const bannerSrc = !isEcosystem ? org?.bannerSrc : undefined;
  const hasLinks = Boolean(href || xHref || articleHref);
  const articleIsInternal = Boolean(articleHref?.startsWith("/"));

  return (
    <div className="flagship-region-modal-overlay" onClick={onClose} role="presentation">
      <aside
        className={`flagship-region-modal-sidebar${bannerSrc ? " has-banner" : ""}${
          isEcosystem ? " is-ecosystem" : ""
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="flagship-region-modal-title"
        onClick={onClose}
      >
        <button type="button" className="flagship-region-modal-close" onClick={onClose} aria-label="Close">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M18 6L6 18"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 6l12 12"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="flagship-region-modal-content" onClick={(event) => event.stopPropagation()}>
          {bannerSrc ? (
            <div className="flagship-region-modal-banner">
              <img src={bannerSrc} alt="" className="flagship-region-modal-banner-image" />
            </div>
          ) : null}

          <div className="flagship-region-modal-body">
            {isEcosystem && ecosystem ? (
              <>
                <div className="flagship-region-modal-eco-top">
                  <img
                    src={ecosystem.logoSrc}
                    alt=""
                    className="flagship-region-modal-eco-logo"
                  />
                  <h2 id="flagship-region-modal-title" className="flagship-region-modal-sr-only">
                    {fullName}
                  </h2>
                </div>
                <div className="flagship-region-modal-bio flagship-region-modal-bio--ecosystem">
                  <p>{summary}</p>
                  {focuses?.length ? (
                    <ul className="flagship-region-modal-focuses">
                      {focuses.map((focus) => (
                        <li key={focus}>{focus}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                {hasLinks ? (
                  <div className="flagship-region-modal-footer flagship-region-modal-footer--ecosystem">
                    <div className="flagship-region-modal-socials">
                      {href ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flagship-region-modal-social"
                          aria-label={`${fullName} website`}
                        >
                          <img src="/images/web_white.png" alt="" />
                        </a>
                      ) : null}
                      {xHref ? (
                        <a
                          href={xHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flagship-region-modal-social"
                          aria-label={`${fullName} on X`}
                        >
                          <img src="/images/x_white.png" alt="" />
                        </a>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <header className="flagship-region-modal-header">
                  <h2 id="flagship-region-modal-title" className="flagship-region-modal-title">
                    {name}
                    <span className="flagship-region-modal-flag" aria-hidden="true">
                      {flagSrc ? (
                        <img src={flagSrc} alt="" className="flagship-region-modal-flag-img" />
                      ) : (
                        flagEmoji
                      )}
                    </span>
                  </h2>
                  {regionLabel ? (
                    <p className="flagship-region-modal-region">{regionLabel}</p>
                  ) : null}
                </header>

                <div className="flagship-region-modal-bio">
                  <p>{summary}</p>
                </div>

                <div className="flagship-region-modal-footer">
                  {involveCta ? <p className="flagship-region-modal-involve">{involveCta}</p> : null}
                  {hasLinks ? (
                    <div className="flagship-region-modal-links">
                      <div className="flagship-region-modal-socials">
                        {href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flagship-region-modal-social"
                            aria-label={`${fullName} website`}
                          >
                            <img src="/images/web_white.png" alt="" />
                          </a>
                        ) : null}
                        {xHref ? (
                          <a
                            href={xHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flagship-region-modal-social"
                            aria-label={`${fullName} on X`}
                          >
                            <img src="/images/x_white.png" alt="" />
                          </a>
                        ) : null}
                      </div>
                      {articleHref ? (
                        articleIsInternal ? (
                          <Link
                            to={articleHref}
                            className="flagship-region-modal-article"
                            onClick={onClose}
                          >
                            {articleLabel ?? "Read our article"}
                          </Link>
                        ) : (
                          <a
                            href={articleHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flagship-region-modal-article"
                          >
                            {articleLabel ?? "Read our article"}
                          </a>
                        )
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default FlagshipRegionModal;
